import { faDesktop, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';

import { getSetting, setSetting } from '@/services/userSettingsStore';

export type ThemeSetting = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
export type DarkStyle = 'classic' | 'midnight';

export const THEME_OPTIONS: { value: ThemeSetting; label: string; icon: typeof faSun }[] = [
  { value: 'light', label: 'Light', icon: faSun },
  { value: 'dark', label: 'Dark', icon: faMoon },
  { value: 'system', label: 'System', icon: faDesktop }
];

export const DARK_STYLE_OPTIONS: { value: DarkStyle; label: string }[] = [
  { value: 'classic', label: 'Classic' },
  { value: 'midnight', label: 'Midnight' }
];

const LEGACY_DARK_MODE_KEY = 'darkMode';

export const DEFAULT_THEME_SETTING: ThemeSetting = 'system';
export const DEFAULT_DARK_STYLE: DarkStyle = 'classic';

export function prefersDarkSystemTheme(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Folds the long-retired `darkMode` flag into the current `theme` setting.
 *
 * Called once when the theme hook mounts rather than from a read, so that
 * reads stay pure -- `useSyncExternalStore` calls its snapshot often and a
 * write from inside one would loop.
 */
export function migrateLegacyDarkMode() {
  let legacyDarkMode: string | null = null;

  try {
    legacyDarkMode = localStorage.getItem(LEGACY_DARK_MODE_KEY);
  } catch {
    return;
  }

  if (legacyDarkMode === null) return;

  try {
    localStorage.removeItem(LEGACY_DARK_MODE_KEY);
  } catch {
    // Nothing to do; the worst case is migrating again next load.
  }

  if (getSetting('theme') === undefined) {
    persistThemeSetting(legacyDarkMode === 'true' ? 'dark' : 'light');
  }
}

/**
 * The stored value is only as trustworthy as localStorage and the server
 * bucket, both of which can hold something we no longer understand: a setting
 * written by an older build, or hand-edited. Anything unrecognized falls back
 * to the default rather than reaching the class-toggling code.
 */
export function toThemeSetting(value: unknown): ThemeSetting {
  return value === 'light' || value === 'dark' || value === 'system'
    ? value
    : DEFAULT_THEME_SETTING;
}

export function toDarkStyle(value: unknown): DarkStyle {
  return value === 'midnight' || value === 'classic' ? value : DEFAULT_DARK_STYLE;
}

export function getStoredThemeSetting(): ThemeSetting {
  return toThemeSetting(getSetting('theme'));
}

export function persistThemeSetting(setting: ThemeSetting) {
  setSetting('theme', setting);
}

export function getStoredDarkStyle(): DarkStyle {
  return toDarkStyle(getSetting('darkStyle'));
}

export function persistDarkStyle(style: DarkStyle) {
  setSetting('darkStyle', style);
}

export function applyResolvedTheme(resolvedTheme: ResolvedTheme, darkStyle: DarkStyle) {
  const isDark = resolvedTheme === 'dark';

  document.body.classList.toggle('dark', isDark);
  document.body.classList.toggle('light', !isDark);
  // Only carry the style class while dark is actually on, so the midnight
  // token overrides can never leak into the light theme.
  document.body.classList.toggle('midnight', isDark && darkStyle === 'midnight');
}
